// A single update, reachable from search results and as a direct link (/story/<id>).
import { useEffect, useState } from "react";
import { relativeTime } from "../lib/relativeTime.ts";
import { shortSource } from "../lib/sourceLabel.ts";
import { LENSES, LENS_LABELS, type Story } from "../../shared/model.ts";
import { PRODUCT_BY_ID } from "../../shared/products.ts";
import { StoryCard } from "../components/StoryCard.tsx";
import { btn } from "../components/ui.tsx";
import { onLinkClick, useStoryIdFromPath } from "../hooks/useRouter.ts";
import { useApp } from "../state/AppState.tsx";

export function StoryPage() {
  const app = useApp();
  const id = useStoryIdFromPath();
  const cached = id ? app.stories.get(id) : undefined;
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">(cached ? "ready" : "loading");

  useEffect(() => {
    if (!id || !app.repo) return;
    if (app.stories.has(id)) return setState("ready");
    setState("loading");
    app.repo
      .getByIds([id])
      .then((found) => {
        app.rememberStories([...found.values()]);
        setState(found.has(id) ? "ready" : "missing");
      })
      .catch(() => setState("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, app.repo]);

  const story: Story | undefined = id ? app.stories.get(id) : undefined;
  useEffect(() => {
    if (story) document.title = `${story.title} · Databricks Pulse`;
  }, [story]);
  // Counts toward "popular" once the story has loaded (missing IDs aren't counted).
  const loadedId = story?.id;
  useEffect(() => {
    if (loadedId) app.trackClick(loadedId, "opens");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedId]);

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-5 sm:px-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <a href="/browse" onClick={onLinkClick} className={btn.ghost}>← Browse</a>
        {/* Summary styles only when distinct summaries exist (not for excerpt-only stories). */}
        {story?.summaryProvenance !== "source_excerpt" && (
        <fieldset>
          <legend className="sr-only">Summary style</legend>
          <div className="inline-flex flex-wrap gap-1 rounded-xl border border-line bg-card p-1">
            {LENSES.map((l) => (
              <label key={l}>
                <input type="radio" name="story-lens" className="peer sr-only" checked={app.prefs.lens === l} onChange={() => app.setPrefs({ lens: l })} />
                <span className="block cursor-pointer rounded-xl px-3 py-1.5 text-sm font-medium text-ink-soft peer-checked:bg-ink peer-checked:text-on-ink peer-focus-visible:outline-3 peer-focus-visible:outline-coral-deep">
                  {LENS_LABELS[l]}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        )}
      </div>

      {state === "loading" && !story ? (
        <p role="status" className="py-16 text-center text-ink-soft">Loading update…</p>
      ) : story ? (
        <>
          <StoryCard story={story} lens={app.prefs.lens} saved={app.isSaved(story.id)} onToggleSave={() => app.toggleSave(story.id)} active headingLevel="h1" />
          {story.products.length > 0 && (
            <nav aria-label="Products" className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-ink-soft">On the Map:</span>
              {story.products.map((p) => (
                <a key={p} href={`/map?product=${p}`} onClick={onLinkClick} className="inline-flex min-h-9 items-center rounded-lg border border-line-strong bg-card px-3 font-medium hover:bg-sand">
                  {PRODUCT_BY_ID.get(p)?.name ?? p}
                </a>
              ))}
            </nav>
          )}
          {story.products[0] && <Related story={story} productId={story.products[0]} />}
        </>
      ) : (
        <div className="py-16 text-center">
          <h1 className="font-display text-3xl font-semibold">{state === "error" ? "This update couldn't be loaded" : "Update not found"}</h1>
          <p className="mt-2 text-ink-soft">{state === "error" ? "Check your connection and try again." : "It may have been unpublished or the link is wrong."}</p>
          <a href="/" onClick={onLinkClick} className={`${btn.primary} mt-5`}>Go to Browse</a>
        </div>
      )}
    </div>
  );
}

/** "More on <product>": the product's next newest updates (one bounded query, 31 reads). */
function Related({ story, productId }: { story: Story; productId: string }) {
  const app = useApp();
  const [items, setItems] = useState<Story[] | null>(null);
  useEffect(() => {
    if (!app.repo) return;
    let live = true;
    app.repo
      .listByProduct(productId, null)
      .then((page) => {
        if (!live) return;
        app.rememberStories(page.stories);
        setItems(page.stories.filter((s) => s.id !== story.id).slice(0, 5));
      })
      .catch(() => live && setItems([]));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, story.id, app.repo]);
  if (!items?.length) return null;
  const name = PRODUCT_BY_ID.get(productId)?.name ?? productId;
  return (
    <section aria-labelledby="related-title" className="mt-8">
      <div className="flex items-center justify-between gap-3">
        <h2 id="related-title" className="text-base font-semibold">More on {name}</h2>
        <a href={`/map?product=${productId}`} onClick={onLinkClick} className={`inline-flex min-h-11 items-center text-sm ${btn.link}`}>Full timeline</a>
      </div>
      <ul className="mt-2 divide-y divide-line rounded-xl border border-line bg-card">
        {items.map((s) => (
          <li key={s.id}>
            <a href={`/story/${s.id}`} onClick={onLinkClick} className="flex items-baseline justify-between gap-4 px-4 py-3 hover:bg-sand/60">
              <span className="text-sm font-medium text-ink">{s.title}</span>
              <span className="shrink-0 text-xs text-ink-soft">{shortSource(s.sourceName, s.contentType)} · {relativeTime(s.publishedAt ?? s.sortAt)}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
